/**
 * RequireRole — guard de acesso à rota (Req 4.4, DD-005, RNF 2, ADR-0001).
 *
 * Usa `canSeeRoute` de `shared/rbac` (Lista Canônica 4.1) para decidir se o
 * usuário pode acessar a rota. Defense-in-depth: a autorização final é do servidor
 * (401/403 são tratados pelos interceptores HTTP).
 *
 * Dois motivos distintos levam a bloqueio, e o destino do redirecionamento
 * diferencia os dois porque a mensagem certa para cada um é diferente:
 *
 * - Funcionalidade indisponível neste ambiente (ver `shared/features/featureAvailability`):
 *   a tela não existe para ninguém aqui, então o comportamento correto é o mesmo
 *   de uma URL desconhecida — cai no dashboard, igual ao catch-all do Router.
 *   Dizer "acesso negado" sugeriria que existe um papel capaz de entrar, o que
 *   seria mentira e convidaria o usuário a pedir permissão que não resolve nada.
 * - Papel insuficiente: aí sim é autorização, e /unauthorized é o destino.
 */

import { Navigate } from "react-router-dom";
import { canSeeRoute, type RouteId, type RbacContext } from "../../shared/rbac/rbac.js";
import { isFeatureAvailable } from "../../shared/features/featureAvailability.js";
import type { ReactNode } from "react";

interface RequireRoleProps {
  /** ID da rota conforme Lista Canônica 4.1. */
  route: RouteId;
  /** Contexto de RBAC do usuário corrente. */
  rbacCtx: RbacContext;
  children: ReactNode;
}

export function RequireRole({ route, rbacCtx, children }: RequireRoleProps) {
  if (!isFeatureAvailable(route)) {
    return <Navigate to="/dashboard" replace />;
  }
  if (!canSeeRoute(route, rbacCtx)) {
    return <Navigate to="/unauthorized" replace />;
  }
  return <>{children}</>;
}
